/**
 * @description
 * Subscription management component for creating, updating, and canceling Stripe subscriptions.
 * Handles subscription lifecycle and billing cycle management.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date 2025-01-08
 * @see admin/src/frontend/components/stripe/SubscriptionManager.test.tsx
 */

import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { api } from '../../services/api';

interface Subscription {
  id: string;
  clientId: string;
  stripeSubscriptionId: string;
  stripeCustomerId: string;
  status: string;
  currentPeriodStart: string | null;
  currentPeriodEnd: string | null;
  cancelAtPeriodEnd: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
  updatedAt: string;
}

interface SubscriptionItem {
  priceId: string;
  quantity: number;
}

interface SubscriptionManagerProps {
  clientId: string;
}

/**
 * Subscription manager component
 */
export function SubscriptionManager({ clientId }: SubscriptionManagerProps) {
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);

  // Form state
  const [items, setItems] = useState<SubscriptionItem[]>([{ priceId: '', quantity: 1 }]);
  const [trialPeriodDays, setTrialPeriodDays] = useState(0);

  const loadSubscriptions = useCallback(async () => {
    try {
      setLoading(true);
      const subscriptions = await api.getSubscriptions(clientId);
      setSubscriptions(subscriptions);
    } catch (error) {
      console.error('Error loading subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const handleCreateSubscription = async () => {
    if (items.some(item => !item.priceId)) {
      toast.error('Please select a price for all items');
      return;
    }

    try {
      setCreating(true);
      
      await api.createSubscription(clientId, {
        items,
        trialPeriodDays,
      });
      
      toast.success('Subscription created successfully');
      setShowCreateForm(false);
      setItems([{ priceId: '', quantity: 1 }]);
      setTrialPeriodDays(0);
      await loadSubscriptions();
    } catch (error) {
      console.error('Error creating subscription:', error);
      toast.error('Failed to create subscription');
    } finally {
      setCreating(false);
    }
  };

  const handleCancelSubscription = async (subscriptionId: string) => {
    if (!confirm('Are you sure you want to cancel this subscription? It will remain active until the end of the current billing period.')) {
      return;
    }

    try {
      await api.cancelSubscription(subscriptionId);
      
      toast.success('Subscription canceled successfully');
      await loadSubscriptions();
    } catch (error) {
      console.error('Error canceling subscription:', error);
      toast.error('Failed to cancel subscription');
    }
  };

  const addSubscriptionItem = () => {
    setItems([...items, { priceId: '', quantity: 1 }]);
  };

  const removeSubscriptionItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateSubscriptionItem = (index: number, field: keyof SubscriptionItem, value: string | number) => {
    const updatedItems = [...items];
    updatedItems[index] = { ...updatedItems[index], [field]: value };
    setItems(updatedItems);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'canceled':
        return 'bg-red-100 text-red-800';
      case 'past_due':
        return 'bg-yellow-100 text-yellow-800';
      case 'unpaid':
        return 'bg-orange-100 text-orange-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="rounded-lg border bg-card p-6">
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-lg border bg-card p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Subscriptions</h3>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          className="rounded-md bg-primary px-3 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
        >
          {showCreateForm ? 'Cancel' : 'Create Subscription'}
        </button>
      </div>

      {/* Create Subscription Form */}
      {showCreateForm && (
        <div className="mb-6 p-4 border rounded-md bg-muted/50">
          <h4 className="text-sm font-medium mb-3">Create New Subscription</h4>
          
          <div className="space-y-3">
            {/* Subscription Items */}
            {items.map((item, index) => (
              <div key={index} className="flex items-center gap-2">
                <select
                  value={item.priceId}
                  onChange={(e) => updateSubscriptionItem(index, 'priceId', e.target.value)}
                  className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  <option value="">Select Price</option>
                  <option value="price_vibesdk_basic">VibeSDK Basic - $29/month</option>
                  <option value="price_vibesdk_standard">VibeSDK Standard - $99/month</option>
                  <option value="price_vibesdk_premium">VibeSDK Premium - $299/month</option>
                </select>
                <input
                  type="number"
                  min="1"
                  value={item.quantity}
                  onChange={(e) => updateSubscriptionItem(index, 'quantity', parseInt(e.target.value) || 1)}
                  className="w-20 rounded-md border border-input bg-background px-3 py-2 text-sm"
                />
                {items.length > 1 && (
                  <button
                    onClick={() => removeSubscriptionItem(index)}
                    className="rounded-md border border-red-200 bg-background px-2 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            
            <button
              onClick={addSubscriptionItem}
              className="text-sm text-primary hover:underline"
            >
              + Add another item
            </button>

            {/* Trial Period */}
            <div>
              <label className="text-sm font-medium">Trial Period (days)</label>
              <input
                type="number"
                min="0"
                max="365"
                value={trialPeriodDays}
                onChange={(e) => setTrialPeriodDays(parseInt(e.target.value) || 0)}
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              />
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                onClick={handleCreateSubscription}
                disabled={creating}
                className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Subscription'}
              </button>
              <button
                onClick={() => setShowCreateForm(false)}
                disabled={creating}
                className="rounded-md border border-input bg-background px-4 py-2 text-sm font-medium transition-colors hover:bg-accent disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Subscriptions List */}
      {subscriptions.length > 0 ? (
        <div className="space-y-3">
          {subscriptions.map((subscription) => (
            <div key={subscription.id} className="border rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-sm">{subscription.stripeSubscriptionId}</span>
                  <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(subscription.status)}`}>
                    {subscription.status}
                  </span>
                </div>
                <div className="flex gap-2">
                  <a
                    href={`https://dashboard.stripe.com/subscriptions/${subscription.stripeSubscriptionId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="rounded-md border border-input bg-background px-3 py-2 text-sm font-medium transition-colors hover:bg-accent"
                  >
                    View in Stripe
                  </a>
                  {subscription.status === 'active' && (
                    <button
                      onClick={() => handleCancelSubscription(subscription.id)}
                      className="rounded-md border border-red-200 bg-background px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50"
                    >
                      Cancel
                    </button>
                  )}
                </div>
              </div>
              
              <div className="grid gap-2 text-sm text-muted-foreground">
                <div className="flex justify-between">
                  <span>Current Period:</span>
                  <span>{formatDate(subscription.currentPeriodStart)} - {formatDate(subscription.currentPeriodEnd)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cancel at Period End:</span>
                  <span>{subscription.cancelAtPeriodEnd ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex justify-between">
                  <span>Created:</span>
                  <span>{formatDate(subscription.createdAt)}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <svg className="h-12 w-12 text-muted-foreground mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h4 className="text-lg font-medium mb-2">No Subscriptions</h4>
          <p className="text-sm text-muted-foreground mb-4">
            This client doesn't have any active subscriptions yet.
          </p>
          <button
            onClick={() => setShowCreateForm(true)}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Create First Subscription
          </button>
        </div>
      )}
    </div>
  );
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */
